import axios from "axios";
import { SERVER_URL } from "Constants";
import { MonitorPaygPrice } from "Hooks/Api/apiPrice";
import { produce } from "immer";
import { accountType } from "Models/Enums";
import { store } from "Models/Store";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import alertToast from "Util/ToastifyUtils";
import { BusinessPaymentSelector } from "../BusinessPaymentSelector/BusinessPaymentSelector";
import { Button, Colors, Display, Sizes } from "../Button/Button";
import { ButtonGroup } from "../Button/ButtonGroup";
import { CreditReportProductDetail } from "../CreditReportSettings/CreditReportProductList";
import Modal from "../Modal/Modal";
import InlineSpinner from "../Spinner/InlineSpinner";
import { ChooseCustomerReportEntity } from "./Customer/ChooseCustomerReportEntity";
import { InterimFormRequestData, InterimFormSection } from "./GenerateCreditReportParts/InterimSection/InterimFormSection";
import { InterimSubmitSection } from "./GenerateCreditReportParts/InterimSection/InterimSubmitSection";
import { GenerateCreditReportCompletion } from "./GenerateCreditReportParts/PayAndGenerateReport/GenerateCreditReportCompletion";
import { MakePaymentForReport } from "./GenerateCreditReportParts/PayAndGenerateReport/MakePaymentForReport";
import {
	ProviderProductSelection
} from "./GenerateCreditReportParts/ProviderProductSelection/ProviderProductSelection";
import { CreditProvider, CreditProviderSection } from "./GenerateCreditReportParts/ProviderSection/CreditProviderSection";
import { ReviewReportSection } from "./GenerateCreditReportParts/ReviewSection/ReviewReportSection";
import { AbrOrAsicSearchResponse } from "../JurisdictionBusinessEntitySearchBox/AustralianEntitySearchBox";
import { JurisdictionSelection } from "./GenerateCreditReportParts/SearchBoxSection/CountrySelection";
import {
	SearchEntitySection
} from "./GenerateCreditReportParts/SearchBoxSection/SearchEntitySection";
import { AustralianEntitySearchBoxSegment } from "../JurisdictionBusinessEntitySearchBox/AustralianEntitySearchBoxSegment";
import { NzbnEntitySearchResponse } from "Hooks/Api/apiBusinessNumber";

export type MonitorCustomerDetail = MonitorPaygPrice & {
	addCustomer: boolean;
}

export type GenerateCreditReportRequestSummary = {
	countryCode: {
		countryCode: string;
		isValid: boolean;
	};
	provider?: CreditProvider;
	requestEntity: {
		lookupDetail?: {
			lookupMethod: "ABN" | "ACN" | "NCN";
			lookupValue: string;
		},
		entityDetail?: SearchEntityDetail;
		isValid: boolean;
	};
	billingEntity: {
		organisationId: string;
		billingEntityId: string;
		billingEntityName: string;
		billingType?: accountType;
		paymentMethodId?: string;
	};
	selectedCreditReports: CreditReportProductDetail[];
	isPaymentRequired: boolean;
	paymentStatus: "pending" | "success" | "failed";
	reportRequestIds: string[];
	interimFormData: InterimFormRequestData;
	monitorCustomer: MonitorCustomerDetail | null;
}

export type SearchEntityDetail = AbrOrAsicSearchResponse | NzbnEntitySearchResponse;

const defaultGenerateCreditReportSummary: GenerateCreditReportRequestSummary = {
	countryCode: {
		countryCode: "AU",
		isValid: true
	},
	requestEntity: {
		isValid: false
	},
	billingEntity: {
		organisationId: "",
		billingEntityId: "",
		billingEntityName: "",
	},
	selectedCreditReports: [],
	isPaymentRequired: true,
	paymentStatus: "pending",
	reportRequestIds: [],
	interimFormData: {
		businessEntityId: "",
		businessEntityName: "",
		companyName: "",
		companyNumber: "",
		companyAddress: "",
		zipCode: "",
		city: "",
		providerProducts: []
	},
	monitorCustomer: null
}

export interface CustomerRequestEntityDetail {
	lookupValue: string;
	lookupMethod: "ABN" | "ACN";
	countryCode: string
	entityName: string;
}

export interface InitialGenerateCreditReportDataForCustomer {
	customerId: string;
	creditReportSubjects: AbrOrAsicSearchResponse[];
	creditReportBillingEntity: {
		organisationId: string;
		businessEntityId: string;
		businessEntityName: string;
		accountType: accountType;
	};
}

export interface GenerateCreditReportRequestSummaryResponse {
	creditReportSubject: {
		lookupValue: string;
		lookupMethod: "ABN" | "ACN" | "NCN";
		countryCode: string;
		entityName: string;
	},
	creditReportBillingEntity: {
		organisationId: string;
		businessEntityId: string;
		businessEntityName: string;
		accountType: accountType;
	},
	creditReportProducts: {
		businessEntityCreditReportSettingsEntityId: string;
		name: string;
		bureau: string;
		type: string;
		previewLink: string;
		originalPrice: number;
		price: number;
		discount: number;
		recommended: boolean;
		available: boolean;
	}[];
	creditProvider: CreditProvider;
	paymentMethodId: string;
	customerId: string;
}

const getDefualtGenerateCreditReportSummary = (summary: GenerateCreditReportRequestSummaryResponse): GenerateCreditReportRequestSummary => {
	const result: GenerateCreditReportRequestSummary = {
		countryCode: {
			countryCode: summary.creditReportSubject.countryCode,
			isValid: true,
		},
		provider: summary.creditProvider && {
			id: summary.creditProvider.id,
			name: summary.creditProvider.name,
			description: summary.creditProvider.description,
			type: summary.creditProvider.type,
			isActive: summary.creditProvider.isActive,
			organisationId: summary.creditProvider.organisationId,
		},
		requestEntity: {
			lookupDetail: {
				lookupMethod: summary.creditReportSubject.lookupMethod,
				lookupValue: summary.creditReportSubject.lookupValue,
			},
			entityDetail: {
				abn: summary.creditReportSubject.lookupMethod === "ABN" ? summary.creditReportSubject.lookupValue : "",
				acn: summary.creditReportSubject.lookupMethod === "ACN" ? summary.creditReportSubject.lookupValue : "",
				identifierNumber: summary.creditReportSubject.lookupValue,
				entityName: summary.creditReportSubject.entityName,
				entityStatus: "N/A",
				address: "N/A",
				lookUpMethod: summary.creditReportSubject.lookupMethod === "NCN"
					? "ABN"
					: summary.creditReportSubject.lookupMethod,
			},
			isValid: true,
		},
		billingEntity: {
			organisationId: summary.creditReportBillingEntity.organisationId,
			billingEntityId: summary.creditReportBillingEntity.businessEntityId,
			billingEntityName: summary.creditReportBillingEntity.businessEntityName,
			billingType: summary.creditReportBillingEntity.accountType,
			paymentMethodId: summary.paymentMethodId || undefined,
		},
		selectedCreditReports: summary.creditReportProducts.map(product => ({
			id: product.businessEntityCreditReportSettingsEntityId,
			name: product.name,
			bureau: product.bureau,
			type: product.type,
			previewLink: product.previewLink,
			originalPrice: product.originalPrice,
			price: product.price,
			discount: product.discount,
			recommended: product.recommended,
			available: product.available,
		})),
		isPaymentRequired: summary.creditReportBillingEntity.accountType === "PAYG",
		paymentStatus: "pending",
		reportRequestIds: [],
		interimFormData: {
			businessEntityId: summary.creditReportBillingEntity.businessEntityId,
			businessEntityName: summary.creditReportBillingEntity.businessEntityName,
			companyName: "",
			companyNumber: "",
			companyAddress: "",
			zipCode: "",
			city: "",
			providerProducts: []
		},
		monitorCustomer: null,
	}

	return result;
}

function getInitialSummaryFromInitialData(data: InitialGenerateCreditReportDataForCustomer): { summary: GenerateCreditReportRequestSummary, stage: GenerateCreditReportStage } {
	const initialRequestEntity = data.creditReportSubjects.length === 1 ? data.creditReportSubjects[0] : undefined;
	const summary: GenerateCreditReportRequestSummary = {
		countryCode: {
			countryCode: "AU",
			isValid: true,
		},
		requestEntity: {
			lookupDetail: !!initialRequestEntity ? {
				lookupMethod: initialRequestEntity.lookUpMethod,
				lookupValue: initialRequestEntity.identifierNumber,
			} : undefined,
			entityDetail: initialRequestEntity ?? undefined,
			isValid: !!initialRequestEntity,
		},
		billingEntity: {
			organisationId: data.creditReportBillingEntity.organisationId,
			billingEntityId: data.creditReportBillingEntity.businessEntityId,
			billingEntityName: data.creditReportBillingEntity.businessEntityName,
			billingType: data.creditReportBillingEntity.accountType,
		},
		selectedCreditReports: [],
		isPaymentRequired: data.creditReportBillingEntity.accountType === "PAYG",
		paymentStatus: "pending",
		reportRequestIds: [],
		interimFormData: {
			businessEntityId: data.creditReportBillingEntity.businessEntityId,
			businessEntityName: data.creditReportBillingEntity.businessEntityName,
			companyName: "",
			companyNumber: "",
			companyAddress: "",
			zipCode: "",
			city: "",
			providerProducts: []
		},
		monitorCustomer: null
	}

	return {
		summary: summary,
		stage: "provider"
	}
}

async function getInitialSummary(id: string):
	Promise<{ summary: GenerateCreditReportRequestSummary, stage: GenerateCreditReportStage }> {
	try {
		const response = await axios.get<GenerateCreditReportRequestSummaryResponse>(
			`${SERVER_URL}/api/credit-report/summary/${id}`
		);
		const summary = getDefualtGenerateCreditReportSummary(response.data);
		if (!summary.provider) {
			return { summary: summary, stage: "provider" };
		}

		if (store.impersonatingOrganisationId && store.impersonatingOrganisationId !== summary.billingEntity.organisationId) {
			return { summary: summary, stage: "billingEntity" };
		}

		return { summary: summary, stage: "review" };
	} catch (error) {
		console.error(error);
		alertToast("Sorry, we could not retrieve the report summary. Please re-enter the details.", "error");
		return { summary: defaultGenerateCreditReportSummary, stage: "countryCode" };
	}
}

async function getInitialSummaryForDirectorRefresh(customerId: string, monitoredId: string): Promise<{ summary: GenerateCreditReportRequestSummary, stage: GenerateCreditReportStage }> {
	try {
		const response = await axios.get<GenerateCreditReportRequestSummaryResponse>(
			`${SERVER_URL}/api/directors/customers/${customerId}/monitored/${monitoredId}/credit-report`);
		const summary = getDefualtGenerateCreditReportSummary(response.data);
		if (!summary.provider) {
			return { summary: summary, stage: "provider" };
		}

		if (
			(!response.data.paymentMethodId && response.data.creditReportBillingEntity.accountType === "PAYG") ||
			(response.data.creditReportProducts.length === 0) ||
			(store.impersonatingOrganisationId && store.impersonatingOrganisationId !== response.data.creditProvider.organisationId)
		) {
			return { summary: summary, stage: "billingEntity" };
		}
		return { summary: summary, stage: "review" };
	} catch (error) {
		console.error(error);
		alertToast("Sorry, we could not retrieve the report summary. Please re-enter the details.", "error");
		return { summary: defaultGenerateCreditReportSummary, stage: "countryCode" };
	}
}

export type GenerateCreditReportStage = "countryCode" | "provider" | "interimForm" | "requestEntity" | "billingEntity" | "review" | "payment" | "generate" | "offlineRequest";

export const GenerateCreditReport: React.FunctionComponent = () => {
	// read from session storage
	const serializedState = sessionStorage.getItem("generateCreditReportState");

	const location = useLocation();
	// read from location state
	const initialData = location.state as {
		id: string,
		stage: GenerateCreditReportStage,
		initialGenerateCreditReportData: InitialGenerateCreditReportDataForCustomer,
		customerId: string,
		monitoredId: string
	} | undefined;

	const queryParams = new URLSearchParams(location.search);
	const availableQueryParams = {
		lookupMethod: queryParams.get("lookupMethod"),
		lookupValue: queryParams.get("lookupValue"),
		countryCode: queryParams.get("countryCode"),
		stage: queryParams.get("stage") as GenerateCreditReportStage | undefined,
	}

	const initialDefaultGenerateCreditReportSummary = useMemo(() => {
		const data = defaultGenerateCreditReportSummary;
		if (availableQueryParams.lookupMethod && availableQueryParams.lookupValue) {
			data.requestEntity.lookupDetail = {
				lookupMethod: availableQueryParams.lookupMethod as "ABN" | "ACN" | "NCN",
				lookupValue: availableQueryParams.lookupValue,
			}
		}

		if (availableQueryParams.countryCode) {
			data.countryCode.countryCode = availableQueryParams.countryCode;
		}

		return data;
	}, [availableQueryParams.lookupMethod, availableQueryParams.lookupValue, availableQueryParams.countryCode]);

	const [isLoading, setIsLoading] = useState(false);
	const [currentStage, setCurrentStage] = useState<GenerateCreditReportStage>(initialData?.stage ?? availableQueryParams.stage ?? "countryCode");
	const [generateCreditReportSummary, setGenerateCreditReportSummary] = useState<GenerateCreditReportRequestSummary>(initialDefaultGenerateCreditReportSummary);
	const [isCustomerGenerate, setIsCustomerGenerate] = useState(false);

	function getLookupName(entityDetail?: SearchEntityDetail): string {
		if (!entityDetail) {
			return "";
		}

		if ("entityName" in entityDetail) {
			return entityDetail.entityName;
		}

		if ("name" in entityDetail) {
			return entityDetail.name;
		}

		return "";
	}

	function getLookupStatus(entityDetail?: SearchEntityDetail): string {
		if (!entityDetail) {
			return "";
		}

		if ("entityStatus" in entityDetail) {
			return entityDetail.entityStatus;
		}

		if ("status" in entityDetail) {
			return entityDetail.status;
		}

		return "";
	}

	function getLookupAddress(entityDetail?: SearchEntityDetail): string {
		if (!entityDetail) {
			return "";
		}

		if ("address" in entityDetail) {
			return entityDetail.address;
		}

		return "";
	}

	function onClose() {
		sessionStorage.removeItem("generateCreditReportState");
		if (
			isCustomerGenerate &&
			initialData &&
			initialData.initialGenerateCreditReportData &&
			initialData.initialGenerateCreditReportData.customerId
		) {
			store.routerHistory.replace(
				`${SERVER_URL}/monitor/customers/${initialData.initialGenerateCreditReportData.customerId}`,
				{
					navigateTo: "reporting"
				}
			);
			return;
		}

		if (initialData && initialData.monitoredId && initialData.customerId) {
			store.routerHistory.replace(
				`${SERVER_URL}/monitor/customers/${initialData.customerId}`,
				{
					navigateTo: "business-information"
				}
			);
			return;
		}

		store.routerHistory.replace(`${SERVER_URL}/hub/credit-reports`);
	}

	function availableCustomerEntityDetails() {
		if (serializedState) {
			try {
				const parsedState = JSON.parse(serializedState) as {
					initialGenerateCreditReportData: InitialGenerateCreditReportDataForCustomer;
					stage: GenerateCreditReportStage;
				};
				if (parsedState.initialGenerateCreditReportData) {
					return parsedState.initialGenerateCreditReportData.creditReportSubjects;
				} else {
					return [];
				}
			} catch (err) {
				console.error(err);
				return [];
			}
		}
		else if (initialData && initialData.initialGenerateCreditReportData) {
			return initialData.initialGenerateCreditReportData.creditReportSubjects;
		} else {
			return [];
		}
	}

	const chargeType = store.getUser?.organisation?.accountType === 'INVOICED'
		? (store.getUser?.organisation?.enforceOnlinePayments ? 'PAYG' : 'INVOICED')
		: 'PAYG'

	const availableSections: {
		stage: GenerateCreditReportStage;
		component: React.ReactNode;
		isStandalone: boolean;
		isAccessible: boolean;
	}[] = [
		{
			stage: "countryCode",
			component: <JurisdictionSelection
				countryCode={generateCreditReportSummary.countryCode.countryCode}
				updateCountryCode={(data, isValid: boolean) => {
					setGenerateCreditReportSummary(
						produce(generateCreditReportSummary, draft => {
							draft.countryCode.countryCode = data;
							draft.countryCode.isValid = isValid;
						})
					);
					if (isValid) {
						setCurrentStage("provider");
					}
				}}
			/>,
			isStandalone: true,
			isAccessible: !isCustomerGenerate,
		},
		{
			stage: "provider",
			component: <CreditProviderSection
				isCustomerGenerate={isCustomerGenerate}
				countryCode={generateCreditReportSummary.countryCode.countryCode}
				provider={generateCreditReportSummary.provider}
				updateProvider={data => {
					setGenerateCreditReportSummary(
						produce(generateCreditReportSummary, draft => {
							draft.provider = data;
						})
					);
				}}
				organisationId={store.getUser?.organisation?.id ?? ""}
				changeStage={newStage => setCurrentStage(newStage)}
			/>,
			isStandalone: true,
			isAccessible: true,
		},
		{
			stage: "interimForm",
			component: <InterimFormSection
				interimFormData={generateCreditReportSummary.interimFormData}
				organisationId={generateCreditReportSummary.billingEntity.organisationId}
				updateInterimFormData={data => {
					setGenerateCreditReportSummary({
						...generateCreditReportSummary,
						interimFormData: data
					});
				}}
				creditProvider={generateCreditReportSummary.provider!}
				countryCode={generateCreditReportSummary.countryCode.countryCode}
			/>,
			isStandalone: true,
			isAccessible: true,
		},
		{
			stage: "requestEntity",
			component: !isCustomerGenerate ? (
				<SearchEntitySection
					countryCode={generateCreditReportSummary.countryCode.countryCode}
					lookupDetail={generateCreditReportSummary.requestEntity.lookupDetail}
					entityDetail={generateCreditReportSummary.requestEntity.entityDetail}
					updateDetail={(
						newLookupDetail,
						isValid,
						entityDetail,
					) => {
						setGenerateCreditReportSummary(
							produce(generateCreditReportSummary, draft => {
								draft.requestEntity.lookupDetail = newLookupDetail;
								draft.requestEntity.entityDetail = entityDetail;
								draft.requestEntity.isValid = isValid;
								draft.billingEntity.organisationId = generateCreditReportSummary.billingEntity.organisationId;
							})
						);
						handleNext();
					}}
				/>
			) : (
				<ChooseCustomerReportEntity
					customerEntityDetails={availableCustomerEntityDetails()}
					value={generateCreditReportSummary.requestEntity.entityDetail as AbrOrAsicSearchResponse}
					setValue={data => {
						setGenerateCreditReportSummary(
							produce(generateCreditReportSummary, draft => {
								draft.requestEntity.lookupDetail = {
									lookupMethod: data.lookUpMethod,
									lookupValue: data.identifierNumber,
								};
								draft.requestEntity.entityDetail = data;
								draft.requestEntity.isValid = true;
							})
						);
						handleNext();
					}}
				/>
			),
			isStandalone: true,
			isAccessible: true
		},
		{
			stage: "billingEntity",
			component: (
				<>
					<AustralianEntitySearchBoxSegment
						title="Credit report subject"
						entityName={getLookupName(generateCreditReportSummary.requestEntity.entityDetail)}
						entityStatus={getLookupStatus(generateCreditReportSummary.requestEntity.entityDetail)}
						entityAddress={getLookupAddress(generateCreditReportSummary.requestEntity.entityDetail)}
						lookupValue={generateCreditReportSummary.requestEntity.lookupDetail?.lookupValue ?? ""}
					/>
					<BusinessPaymentSelector
						// For super user to filter out business that they are impersonating
						filterByOrgId={store.getUser?.organisation?.id ?? ""}
						type={chargeType}
						value={generateCreditReportSummary.billingEntity.billingEntityId}
						setValue={data => {
							setGenerateCreditReportSummary(
								produce(generateCreditReportSummary, draft => {
									draft.billingEntity.organisationId = data.organisationId;
									draft.billingEntity.billingEntityId = data.businessId;
									draft.billingEntity.billingEntityName = data.businessName;
									draft.billingEntity.organisationId = data.organisationId;
									draft.billingEntity.billingType = chargeType;
									draft.selectedCreditReports = [];
									draft.isPaymentRequired = chargeType === 'PAYG';
									draft.paymentStatus = chargeType === "PAYG" ? "pending" : "success";
									if (data.paymentMethodId) draft.billingEntity.paymentMethodId = data.paymentMethodId;
								})
							);
						}}
					/>
					{(generateCreditReportSummary.billingEntity.billingEntityId.length > 0 &&
						(!generateCreditReportSummary.isPaymentRequired || (generateCreditReportSummary.isPaymentRequired && generateCreditReportSummary.billingEntity.paymentMethodId != null)) &&
						generateCreditReportSummary.billingEntity.billingType &&
						generateCreditReportSummary.provider) && (
						<ProviderProductSelection
							providerId={generateCreditReportSummary.provider.id}
							businessEntityId={generateCreditReportSummary.billingEntity.billingEntityId}
							countryCode={generateCreditReportSummary.countryCode.countryCode}
							lookupValue={generateCreditReportSummary.requestEntity.lookupDetail?.lookupValue ?? ""}
							lookupMethod={generateCreditReportSummary.requestEntity.lookupDetail?.lookupMethod ?? "ABN"}
							billingType={generateCreditReportSummary.billingEntity.billingType}
							selectedCreditReports={generateCreditReportSummary.selectedCreditReports}
							updateSelectedCreditReports={data => {
								setGenerateCreditReportSummary(
									produce(generateCreditReportSummary, draft => {
										draft.selectedCreditReports = data;
									})
								)
							}}
						/>
					)}
				</>
			),
			isStandalone: false,
			isAccessible: true
		},
		{
			stage: "review",
			component: <ReviewReportSection
				creditReportSubject={{
					name: getLookupName(generateCreditReportSummary.requestEntity.entityDetail),
					status: getLookupStatus(generateCreditReportSummary.requestEntity.entityDetail),
					address: getLookupAddress(generateCreditReportSummary.requestEntity.entityDetail),
					lookupValue: generateCreditReportSummary.requestEntity.lookupDetail?.lookupValue ?? "",
					lookupMethod: generateCreditReportSummary.requestEntity.lookupDetail?.lookupMethod ?? "ABN",
				}}
				billingEntityName={generateCreditReportSummary.billingEntity.billingEntityName}
				orgAccountType={generateCreditReportSummary.billingEntity.billingType!}
				orgId={generateCreditReportSummary.billingEntity.organisationId}
				businessEntityId={generateCreditReportSummary.billingEntity.billingEntityId}
				selectedProducts={generateCreditReportSummary.selectedCreditReports}
				removeProduct={product => {
					setGenerateCreditReportSummary(
						produce(generateCreditReportSummary, draft => {
							draft.selectedCreditReports = draft.selectedCreditReports.filter(
								selectedProduct => selectedProduct.id !== product.id
							);
						})
					)
				}}
				monitorCustomer={generateCreditReportSummary.monitorCustomer}
				handleMonitorCustomer={value => {
					setGenerateCreditReportSummary(
						produce(generateCreditReportSummary, draft => {
							draft.monitorCustomer = value;
						})
					);
				}}
				moveBack={() => setCurrentStage("billingEntity")}
			/>,
			isStandalone: true,
			isAccessible: true
		},
		{
			stage: "payment",
			component: <MakePaymentForReport
				updateDetail={(status, reportRequestIds) => {
					setGenerateCreditReportSummary(
						produce(generateCreditReportSummary, draft => {
							draft.paymentStatus = status;
							draft.reportRequestIds = reportRequestIds;
						})
					)
				}}
				generateCreditReportSummary={generateCreditReportSummary}
				changeStage={newStage => setCurrentStage(newStage)}
				customerId={initialData?.initialGenerateCreditReportData?.customerId}
			/>,
			isStandalone: true,
			isAccessible: generateCreditReportSummary.billingEntity.billingType === "PAYG",
		},
		{
			stage: "generate",
			component: <GenerateCreditReportCompletion
				creditReportSubject={{
					lookupValue: generateCreditReportSummary.requestEntity.lookupDetail?.lookupValue ?? "",
					lookupMethod: generateCreditReportSummary.requestEntity.lookupDetail?.lookupMethod ?? "ABN",
					countryCode: generateCreditReportSummary.countryCode.countryCode,
					entityName: getLookupName(generateCreditReportSummary.requestEntity.entityDetail),
				}}
				reportRequestIds={generateCreditReportSummary.reportRequestIds}
				changeStage={newStage => setCurrentStage(newStage)}
				customerId={initialData?.initialGenerateCreditReportData?.customerId}
			/>,
			isStandalone: true,
			isAccessible: true,
		},
		{
			stage: "offlineRequest",
			component: <InterimSubmitSection
				submitData={generateCreditReportSummary.interimFormData}
				countryCode={generateCreditReportSummary.countryCode.countryCode}
				changeStage={newStage => setCurrentStage(newStage)}
				onClose={onClose}
			/>,
			isStandalone: true,
			isAccessible: true,
		}
	]

	const renderCurrentSection = () => {
		if (isLoading) {
			return <InlineSpinner />;
		}
		const nodes: React.ReactNode[] = [];
		const currentSection = availableSections.find(section => section.stage === currentStage);
		if (currentSection && currentSection.isStandalone) {
			nodes.push(
				<Fragment key={currentSection.stage}>
					{currentSection.component}
				</Fragment>);
			return nodes;
		}


		// get the index of the current section
		const currentIndex = availableSections.findIndex(section => section.stage === currentStage);

		// get the previous consecutive  section
		const previousSections: React.ReactNode[] = [];
		for (let i = currentIndex - 1; i >= 0; i--) {
			const section = availableSections[i];
			if (section.isStandalone) {
				break;
			}

			if (!section.isAccessible) {
				continue;
			}

			previousSections.unshift(
				<Fragment key={section.stage}>
					{section.component}
				</Fragment>);
		}

		nodes.push(...previousSections);
		if (currentSection) {
			nodes.push(
				<Fragment key={currentSection.stage}>
					{currentSection.component}
				</Fragment>);
		}
		return nodes;
	}

	const handleBack = () => {
		switch (currentStage) {
			case "countryCode":
				onClose();
				break;
			case "provider":
				if (isCustomerGenerate) {
					onClose();
					break;
				}
				setGenerateCreditReportSummary(produce(generateCreditReportSummary, draft => {
					draft.provider = undefined;
				}));
				setCurrentStage("countryCode");
				break;
			case "requestEntity":
				setGenerateCreditReportSummary(produce(generateCreditReportSummary, draft => {
					draft.requestEntity = {
						lookupDetail: undefined,
						entityDetail: undefined,
						isValid: false
					}
				}));
				setCurrentStage("provider");
				break;
			case "interimForm":
				setGenerateCreditReportSummary(produce(generateCreditReportSummary, draft => {
					draft.interimFormData = {
						businessEntityId: generateCreditReportSummary.billingEntity.billingEntityId,
						businessEntityName: generateCreditReportSummary.billingEntity.billingEntityName,
						companyName: "",
						companyNumber: "",
						companyAddress: "",
						zipCode: "",
						city: "",
						providerProducts: []
					}
				}));
				setCurrentStage("provider");
				break;
			case "billingEntity":
				if (isCustomerGenerate) {
					setGenerateCreditReportSummary(produce(generateCreditReportSummary, draft => {
						draft.requestEntity = {
							lookupDetail: undefined,
							entityDetail: undefined,
							isValid: false
						}
					}));
					setCurrentStage("requestEntity");
					break;
				}
				setGenerateCreditReportSummary(produce(generateCreditReportSummary, draft => {
					draft.requestEntity = {
						lookupDetail: undefined,
						entityDetail: undefined,
						isValid: false
					}
					draft.billingEntity = { ...draft.billingEntity, billingEntityId: "", billingEntityName: "", billingType: undefined };
				}));
				setCurrentStage("requestEntity");
				break;
			case "review":
				setCurrentStage("billingEntity");
				break;
			default:
				break;
		}
	}

	const handleNext = () => {
		switch (currentStage) {
			case "countryCode":
				setCurrentStage("provider");
				break;
			case "provider":
				if (generateCreditReportSummary.provider) {
					const isCountryCodeInternational = generateCreditReportSummary.countryCode.countryCode !== "AU" && generateCreditReportSummary.countryCode.countryCode !== "NZ";
					const isProviderCoface = generateCreditReportSummary.provider.name.toLowerCase() === "coface";
					const isInterimFormPath = isCountryCodeInternational || isProviderCoface;
					setCurrentStage(isInterimFormPath ? "interimForm" : "requestEntity");
					break;
				}
				setCurrentStage("requestEntity");
				break;
			case "requestEntity":
				setCurrentStage("billingEntity");
				break;
			case "interimForm":
				setCurrentStage("offlineRequest");
				break;
			case "billingEntity":
				setCurrentStage("review");
				break;
			case "review":
				setCurrentStage("payment");
				break;
			case "generate":
				onClose();
				break;
			case "offlineRequest":
				onClose();
				break;
			default:
				break;
		}
	}

	const handleNextDisabled = () => {

		const {
			billingEntity,
			isPaymentRequired,
			interimFormData
		} = generateCreditReportSummary;

		const isBillingSectionValid = billingEntity.billingEntityId.length > 0
			&& (!isPaymentRequired || (isPaymentRequired && billingEntity.paymentMethodId))
			&& billingEntity.organisationId.length > 0
			&& generateCreditReportSummary.selectedCreditReports.length > 0;

		switch (currentStage) {
			case "countryCode":
				return !generateCreditReportSummary.countryCode.isValid;
			case "requestEntity":
				return !generateCreditReportSummary.requestEntity.isValid;
			case "billingEntity":
				return !isBillingSectionValid
			case "review":
				return !isBillingSectionValid;
			case "provider":
				return !generateCreditReportSummary.provider;
			case "interimForm":
				return !interimFormData.companyName ||
					interimFormData.providerProducts.length === 0;
			default:
				return true;
		}
	}

	const getModalTitle = () => {
		switch (currentStage) {
			case "review":
				return "Review & Confirm Reports";
			default:
				return "Generate Credit Report";
		}
	}

	useEffect(() => {
		if (serializedState) {
			try {
				setIsLoading(true);
				const state: { initialGenerateCreditReportData: InitialGenerateCreditReportDataForCustomer, stage: GenerateCreditReportStage } = JSON.parse(serializedState);
				const response = getInitialSummaryFromInitialData(state.initialGenerateCreditReportData);
				setGenerateCreditReportSummary(response.summary);
				setCurrentStage(response.stage);
				setIsCustomerGenerate(true);
			} catch (error) {
				console.error("Failed to parse state:", error);
			} finally {
				setIsLoading(false);
			}
		}

		if (initialData && initialData.id) {
			const retrieveInitialSummary = async () => {
				setIsLoading(true);
				const response = await getInitialSummary(initialData.id);
				setGenerateCreditReportSummary(response.summary);
				setCurrentStage(response.stage);
				setIsLoading(false);
			}

			retrieveInitialSummary();
		}
		if (initialData && initialData.initialGenerateCreditReportData) {
			setIsLoading(true);
			const response = getInitialSummaryFromInitialData(initialData.initialGenerateCreditReportData);
			setGenerateCreditReportSummary(response.summary);
			setCurrentStage(response.stage);
			setIsCustomerGenerate(true);
			setIsLoading(false);
		}

		if (initialData && initialData.monitoredId && initialData.customerId) {
			const retrieveInitialSummary = async () => {
				setIsLoading(true);
				const response = await getInitialSummaryForDirectorRefresh(initialData.customerId, initialData.monitoredId);
				setGenerateCreditReportSummary(response.summary);
				setCurrentStage(response.stage);
				setIsLoading(false);
			}

			retrieveInitialSummary();
		}

		return () => {
			setGenerateCreditReportSummary(defaultGenerateCreditReportSummary);
			setCurrentStage("countryCode");
			setIsCustomerGenerate(false);
			setIsLoading(false);
		}
	}, [initialData, serializedState]);

	return (
		<Modal className={`generate-credit-report ${currentStage}`} label="" isOpen>
			<div className="modal-header">
				<h3>{getModalTitle()}</h3>
				{!(currentStage === 'payment' || currentStage === 'generate' || currentStage === "offlineRequest") && (

					<Button
						className="modal-close"
						key="header-cancel"
						icon={{ icon: 'cross', iconPos: 'icon-only' }}
						display={Display.Text}
						onClick={onClose}
						sizes={Sizes.Medium}
						colors={Colors.Error}
						labelVisible={false}
					>
						Cancel
					</Button>
				)}
			</div>

			<div className={`modal-body ${currentStage}`}>
				{renderCurrentSection()}
			</div>
			{!(currentStage === "generate" || currentStage === "payment" || currentStage === "offlineRequest") && (
				<ButtonGroup className="modal-footer">
					<Button
						colors={Colors.Primary}
						sizes={Sizes.Medium}
						display={Display.Outline}
						onClick={handleBack}
					>
						{currentStage === "countryCode" || (currentStage === "requestEntity" && isCustomerGenerate) ? "Cancel" : "Back"}
					</Button>
					<Button
						onClick={handleNext}
						colors={Colors.Primary}
						display={Display.Solid}
						disabled={handleNextDisabled()}
					>

						{currentStage === "review" ? "Confirm and pay" : `Proceed`}
					</Button>
				</ButtonGroup>
			)}
		</Modal>
	)
}
